import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Reveal } from "@/components/Motion";

// Update this in your @/data/content file — replace the old 4-domain DOMAINS
// export with this 5-domain version so weights match the source material.
const DOMAINS = [
  { n: "01", title: "AI Fundamentals & Architecture", weight: "15%" },
  { n: "02", title: "AI Security & Threat Landscape", weight: "30%" },
  { n: "03", title: "Secure AI Engineering & Agentic Orchestration", weight: "20%" },
  { n: "04", title: "AI Risk Management, Governance & Ethics", weight: "15%" },
  { n: "05", title: "AI Audit, Compliance & Operations", weight: "20%" },
];
  
const ExamWeights = () => {
  const gridRef = useRef(null);
  const inView = useInView(gridRef, { once: true, amount: 0.3 });

  return (
    <section
      id="exam-weights"
      data-testid="exam-weights"
      className="relative py-28 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl">
          <Reveal>
            <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#16fcfb]">
              [ 007 ] HCASP Examination Weights
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-display font-700 text-4xl md:text-5xl leading-[1.05] tracking-tight text-white">
              Five domains, One roadmap.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 font-body text-base text-white/65 leading-relaxed">
              The HCASP exam moves through five domains in sequence, from
              foundational architecture to audit and compliance , each
              weighted by its share of the real world attack surface.
            </p>
          </Reveal>
        </div>

        {/* Timeline */}
        <div ref={gridRef} className="mt-20 md:mt-24">
          {/* ---- Desktop / tablet: horizontal rail ---- */}
          <div className="hidden md:flex items-start">
            {DOMAINS.map((d, i) => (
              <div
                key={d.n}
                data-testid={`weight-domain-${d.n}`}
                className="flex flex-col items-start flex-1"
              >
                {/* circle + connecting line share one row so the line meets
                    the circle's edge exactly, instead of the wider text column */}
                <div className="flex items-center w-full">
                  <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#3d414f]">
                    <motion.div
                      className="absolute inset-0 rounded-full border border-[#16fcfb]"
                      initial={{ opacity: 0, scale: 0.6 }}
                      animate={
                        inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.6 }
                      }
                      transition={{ duration: 0.5, delay: 0.15 + i * 0.12 }}
                    />
                    <span className="font-mono2 text-[11px] tracking-[0.1em] text-white">
                      D{d.n}
                    </span>
                  </div>

                  {i < DOMAINS.length - 1 && (
                    <div className="relative h-px flex-1 bg-[#3d414f] overflow-hidden">
                      <motion.div
                        className="absolute inset-y-0 left-0 bg-[#16fcfb]"
                        initial={{ width: 0 }}
                        animate={{ width: inView ? "100%" : 0 }}
                        transition={{ duration: 0.6, delay: 0.3 + i * 0.12, ease: "easeOut" }}
                      />
                    </div>
                  )}            
                </div>

                <Reveal delay={0.08 * i}>
                  <div className="pr-6 lg:pr-10">
                    <h3 className="mt-6 font-display font-600 text-sm text-white leading-snug">
                      {d.title}
                    </h3>

                    <div className="mt-4 font-display font-900 text-3xl lg:text-4xl text-[#16fcfb] text-glow-cyan">
                      {d.weight}
                    </div>
                  </div>
                </Reveal>
              </div>
            ))}
          </div>

          {/* ---- Mobile: vertical rail ---- */}
          <div className="md:hidden">
            {DOMAINS.map((d, i) => (
              <Reveal key={d.n} delay={0.08 * i}>
                <div
                  data-testid={`weight-domain-mobile-${d.n}`}
                  className="relative flex gap-5 pb-14 last:pb-0"
                >
                  <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#3d414f]">
                    <motion.div
                      className="absolute inset-0 rounded-full border border-[#16fcfb]"
                      initial={{ opacity: 0, scale: 0.6 }}
                      animate={
                        inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.6 }
                      }
                      transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
                    />
                    <span className="font-mono2 text-[10px] tracking-[0.1em] text-white">
                      D{d.n}
                    </span>
                  </div>

                  {/* absolute + inset-0 so this runs all the way from this
                      circle's bottom through the gap into the next circle,
                      instead of stopping short at the padding boundary */}
                  {i < DOMAINS.length - 1 && (
                    <div className="absolute left-[21px] top-11 bottom-0 w-px bg-[#3d414f] overflow-hidden">
                      <motion.div
                        className="absolute inset-x-0 top-0 bg-[#16fcfb]"
                        initial={{ height: 0 }}
                        animate={{ height: inView ? "100%" : 0 }}
                        transition={{ duration: 0.5, delay: 0.25 + i * 0.1, ease: "easeOut" }}
                      />
                    </div>
                  )}

                  <div className="pt-1">
                    <h3 className="font-display font-600 text-sm text-white leading-snug">
                      {d.title}
                    </h3>
                    <div className="mt-3 font-display font-900 text-2xl text-[#16fcfb] text-glow-cyan">
                      {d.weight}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExamWeights;